/**
 * Panel inferior fijo (RR-28), como el de WOX pero más bajo: una sola barra con el equipo (nombre,
 * placa y modelo) y sus pestañas «Información», «Informe», «Sensores externos», «Eventos de
 * telemetría» y «Alertas de dispositivo».
 *
 * Está SIEMPRE, haya o no un equipo elegido; sin equipo, el cuerpo dice qué hacer. Solo se
 * contrae cuando la persona lo pide. No flota sobre el mapa: está debajo, en el flujo de la página.
 * El mapa se achica en vez de quedar tapado, y como es el mismo elemento siempre, achicarlo no
 * vuelve a cargar Google Maps.
 *
 * Solo lectura sobre el equipo: ninguna pestaña le manda nada al GPS (constitución, Art. 6).
 */

import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { RastreoEventsResponse, RastreoHealth, RastreoTrackResponse } from "@core/contracts/rastreo.ts";
import { DEFAULT_POSITION_COLUMNS, type PositionFieldKey, type RastreoRow } from "@core/rastreo/registro.ts";
import { ExternalSensorsTab, InfoTab } from "./device-sheet.tsx";
import type { AlertRulesApi } from "./alert-rules.ts";
import { AlertsTab, PeriodBar, periodHasAlarms, ReportTab, TelemetryTab, type PeriodControls } from "./rastreo-reports.tsx";
import { CarIcon, ChevronIcon, CloseIcon } from "./rastreo-icons.tsx";

export type DockTab = "informacion" | "informe" | "sensores" | "telemetria" | "alertas";

const TABS: ReadonlyArray<{ id: DockTab; label: string }> = [
  { id: "informacion", label: "Información" },
  { id: "informe", label: "Informe" },
  { id: "sensores", label: "Sensores externos" },
  { id: "telemetria", label: "Eventos de telemetría" },
  { id: "alertas", label: "Alertas de dispositivo" },
];

/** Las pestañas que dependen de las fechas del período. */
export const PERIOD_TABS: ReadonlySet<DockTab> = new Set(["informe", "telemetria", "alertas"]);

export function VehicleDock({
  row,
  now,
  tab,
  onTab,
  open,
  onToggleOpen,
  onClose,
  onEdit,
  onRegister,
  live = null,
  dates,
  track,
  events,
  alertRules,
  health,
}: {
  /** El equipo elegido, o `null`: el panel sigue ahí, con las pestañas, esperando que se elija uno. */
  row: RastreoRow | null;
  now: number;
  tab: DockTab;
  onTab: (tab: DockTab) => void;
  /** Abierto: se ve la pestaña elegida. Contraído: solo la barra. */
  open: boolean;
  onToggleOpen: () => void;
  /** Deja de elegir el equipo. El panel no desaparece. */
  onClose: () => void;
  onEdit: () => void;
  onRegister: () => void;
  /** El estado del recorrido en vivo: una franja fina bajo la barra, visible aunque el panel esté contraído. */
  live?: ReactNode;
  dates: PeriodControls;
  track: UseQueryResult<RastreoTrackResponse>;
  events: UseQueryResult<RastreoEventsResponse>;
  /** Las reglas de alerta del equipo elegido: el aviso de cada una y las que se agregaron. */
  alertRules: AlertRulesApi;
  health: RastreoHealth | null;
}) {
  const uid = useId();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // Las columnas del informe se eligen una vez: cambiar de pestaña o de equipo no las reinicia.
  const [columns, setColumns] = useState<PositionFieldKey[]>(DEFAULT_POSITION_COLUMNS);
  // Un GPS que no mandó alarmas en el período no muestra las columnas de alarma del informe. La
  // pestaña «Alertas de dispositivo» está siempre: es donde se configuran las alertas del equipo.
  const alarms = row ? periodHasAlarms(events, track) : null;
  const tabs = TABS;
  const detail = row ? [row.equipo?.placa, row.equipo?.modelo ?? row.vehicle?.model].filter(Boolean).join(" · ") : "";

  // Flechas, Inicio y Fin mueven entre pestañas, como en cualquier tablist.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = tabs.findIndex((item) => item.id === tab);
    const target =
      event.key === "ArrowRight" ? (current + 1) % tabs.length
      : event.key === "ArrowLeft" ? (current - 1 + tabs.length) % tabs.length
      : event.key === "Home" ? 0
      : event.key === "End" ? tabs.length - 1
      : -1;
    if (target < 0) return;
    event.preventDefault();
    onTab(tabs[target]!.id);
    tabRefs.current[target]?.focus();
  };

  const buttonClass = "flex size-9 shrink-0 items-center justify-center rounded-xl text-white/80 hover:bg-white/10 hover:text-white";

  return (
    <section
      aria-label={row ? `Panel de ${row.name}` : "Panel de equipos"}
      className="flex shrink-0 flex-col border-t border-gd-line bg-white shadow-[0_-8px_24px_rgba(11,30,45,0.10)]"
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-0 bg-gd-deep px-2 pt-1 text-white sm:flex-nowrap sm:pt-0">
        <span aria-hidden="true" className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-gd-cyan">
          <CarIcon className="size-4" />
        </span>
        <h2 className="min-w-0 flex-1 truncate font-display text-[14px] font-bold text-white sm:max-w-[30%] sm:flex-none">
          {row ? row.name : <span className="font-normal text-white/70">Ningún equipo elegido</span>}
          {detail ? <span className="ml-2 text-[12px] font-normal text-white/70">{detail}</span> : null}
        </h2>
        <div
          role="tablist"
          aria-label="Datos del equipo"
          onKeyDown={onKeyDown}
          className="gd-scroll-x order-last flex w-full min-w-0 gap-0.5 sm:order-none sm:w-auto sm:flex-1"
        >
          {tabs.map((item, index) => {
            const active = item.id === tab;
            return (
              <button
                key={item.id}
                ref={(node) => {
                  tabRefs.current[index] = node;
                }}
                id={`${uid}-tab-${item.id}`}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={`${uid}-panel`}
                tabIndex={active ? 0 : -1}
                onClick={() => {
                  onTab(item.id);
                  if (!open) onToggleOpen();
                }}
                className={`min-h-9 shrink-0 rounded-t-lg px-3 text-[13px] font-semibold whitespace-nowrap transition-colors ${
                  active ? "bg-white text-gd-blue" : "text-white/75 hover:bg-white/10 hover:text-white"
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
        <button type="button" onClick={onToggleOpen} aria-expanded={open} aria-controls={`${uid}-panel`} className={buttonClass}>
          <ChevronIcon open={open} className="size-5" />
          <span className="sr-only">{open ? "Contraer el panel" : "Expandir el panel"}</span>
        </button>
        {row ? (
          <button type="button" onClick={onClose} className={buttonClass}>
            <CloseIcon />
            <span className="sr-only">Cerrar el panel</span>
          </button>
        ) : null}
      </header>

      {row && live ? <div className="border-b border-gd-line bg-gd-mist px-3 py-1">{live}</div> : null}

      <div
        id={`${uid}-panel`}
        role="tabpanel"
        aria-labelledby={`${uid}-tab-${tab}`}
        hidden={!open}
        className="h-[26dvh] min-h-[150px] max-h-[260px] overflow-y-auto"
      >
        {!row ? (
          <p className="px-4 py-4 text-[14px] text-gd-muted">
            Elige un equipo en la lista o en el mapa para ver su información, su informe, sus eventos y sus alertas.
          </p>
        ) : (
          <>
            {PERIOD_TABS.has(tab) ? (
              <div className="flex flex-wrap items-end gap-x-4 gap-y-1 border-b border-gd-line bg-white px-4 py-1.5">
                <PeriodBar dates={dates} />
              </div>
            ) : null}
            {tab === "informacion" ? <InfoTab row={row} now={now} onEdit={onEdit} onRegister={onRegister} /> : null}
            {tab === "informe" ? <ReportTab row={row} dates={dates} track={track} health={health} now={now} columns={columns} onColumns={setColumns} alarms={alarms} /> : null}
            {tab === "sensores" ? <ExternalSensorsTab row={row} /> : null}
            {tab === "telemetria" ? <TelemetryTab row={row} dates={dates} events={events} track={track} rules={alertRules} /> : null}
            {tab === "alertas" ? <AlertsTab row={row} dates={dates} events={events} track={track} rules={alertRules} /> : null}
          </>
        )}
      </div>
    </section>
  );
}
